import {useTranslation} from '@aboutyou/i18n';
import {useVipLandingPageRoute} from '@aboutyou/router';
import {
  Button,
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  GenericIconName,
  Icon,
  IconName,
  Text,
  UnstyledLink,
  desktopAndUp,
  higherDesktopAndUp,
} from '@aboutyou/ui-components';
import {useCustomerIsVip} from '@aboutyou/user';
import React, {useState} from 'react';
import styled, {css} from 'styled-components';
import {useLogoutHandler} from '../../../../features/Authentication/hooks/useLogoutHandler';
import {useShowBandData} from '../hooks/useShowBankData';

export interface EnableSettingsModalProps {
  onClose: VoidFunction;
}

export const FlyoutLoggedIn: React.FC<{
  enableSettingsModalComponent: (
    props: EnableSettingsModalProps,
  ) => React.ReactNode;
  enableBankDataRequest: boolean;
  onLogout: VoidFunction;
}> = ({enableSettingsModalComponent, enableBankDataRequest, onLogout}) => {
  const {t} = useTranslation();
  const {logout} = useLogoutHandler();
  const isVip = useCustomerIsVip();
  const vipLandingPageRoute = useVipLandingPageRoute();
  const showBankData = useShowBandData(enableBankDataRequest);
  const [enableSettingsModal, setEnableSettingsModal] = useState(false);

  return (
    <Container>
      <NavItem
        label={t('Your orders')}
        icon={GenericIconName.UspFreeShipping}
        href="/a/orders"
      />

      {showBankData && (
        <NavItem
          label={t('Bank information')}
          icon={GenericIconName.CreditCard}
          href="/a/bank-data"
        />
      )}
      <NavItem
        label={t('Profile & security')}
        icon={GenericIconName.Lock}
        href="/a/profile"
      />

      <NavItem
        label={t('Settings')}
        icon={GenericIconName.Settings}
        onClick={() => {
          setEnableSettingsModal(true);
        }}
      />

      {enableSettingsModal &&
        enableSettingsModalComponent({
          onClose: () => setEnableSettingsModal(false),
        })}

      <NavItem label={t('Help')} icon={GenericIconName.Help} href="/h" />

      {isVip && (
        <NavItem
          label={t('Your VIP benefits')}
          icon={GenericIconName.StarOutline}
          href={vipLandingPageRoute}
        />
      )}

      <HorizontalRuler />

      <Button
        data-testid="LogoutButton"
        type="secondary"
        onClick={() => {
          logout();
          onLogout();
        }}
      >
        {t('Log out')}
      </Button>
    </Container>
  );
};

const NavItem: React.FC<{
  label: string;
  icon: IconName;
  href?: string;
  onClick?: VoidFunction;
}> = ({label, icon, href = '', onClick}) => {
  const navItemContent = (
    <>
      <NavItemIcon asset={icon} size="S" />
      <NavItemLabel>{label}</NavItemLabel>
    </>
  );

  const dataTestId = `NavLabel_${label}`;

  if (href) {
    return (
      <NavItemLink to={href} data-testid={dataTestId}>
        {navItemContent}
      </NavItemLink>
    );
  }

  return (
    <NavItemButton onClick={onClick} data-testid={dataTestId}>
      {navItemContent}
    </NavItemButton>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const HorizontalRuler = styled.hr`
  border-top: 0px;
  box-shadow: inset 0 -1px 0 0 ${COLORS.Grey3};
`;

const NavItemIcon = styled(Icon)`
  fill: ${COLORS.Grey1};
  color: ${COLORS.Grey1};
`;

const NavItemLabel = styled(Text)`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumBold};
`;

const navItemStyles = css`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 180px;
  height: 48px;
  padding: 10px;

  ${desktopAndUp(css`
    width: 190px;
  `)}

  ${higherDesktopAndUp(css`
    width: 210px;
  `)}

  :hover {
    ${NavItemIcon} {
      fill: ${COLORS.Black};
      color: ${COLORS.Black};
    }

    ${NavItemLabel} {
      font-weight: ${FONT_WEIGHTS.heavy};
    }
  }
`;

const NavItemLink = styled(UnstyledLink)`
  ${navItemStyles}
`;

const NavItemButton = styled.button`
  ${navItemStyles}
`;
